// Orchestrates auth bootstrap: account → token validation → advisor fetch
// Ported from advisor-3.0/src/App.js auth flow
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { useAccount } from 'src/api/hooks/useAccount';
import { getOneClickAccountIdOverride, useAdvisorBootstrap } from 'src/api/hooks/useAdvisor';
import SessionTimeout from 'src/components/SessionTimeout/SessionTimeout';
import { getAuthToken, validateToken } from 'src/utility/auth';

const ROLES_WITH_PLATFORM_ACCESS = ['FA', 'Ops', 'Admin'];

type AuthState = 'pending' | 'valid' | 'invalid';

export default function AuthGate() {
  const isLogOut =
    new URLSearchParams(window.location.search).get('logout') === 'true';

  const { data: account, isLoading: accountLoading, isError: accountError } = useAccount();

  const bypassAuthentication = Boolean(account?.authenticationRequiredByPass);
  const oneClickAccountId = getOneClickAccountIdOverride() || account?.account.oneClickAccountId;

  const [authState, setAuthState] = useState<AuthState>('pending');

  useEffect(() => {
    if (!account) return;
    if (bypassAuthentication) {
      setAuthState('valid');
      return;
    }
    if (!getAuthToken()) {
      setAuthState('invalid');
      return;
    }
    let cancelled = false;
    validateToken()
      .then((ok) => {
        if (!cancelled) setAuthState(ok ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setAuthState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [account, bypassAuthentication]);

  const advisorEnabled =
    !isLogOut && Boolean(account) && (authState === 'valid' || bypassAuthentication);
  const {
    data: advisor,
    isLoading: advisorLoading,
    isError: advisorError,
  } = useAdvisorBootstrap({
    oneClickAccountId,
    bypassAuthentication,
    enabled: advisorEnabled,
  });

  if (isLogOut) {
    return (
      <CenteredMessage>
        <strong>You have signed out successfully.</strong>
      </CenteredMessage>
    );
  }

  if (accountError) {
    return (
      <CenteredMessage>
        The account you are trying to access does not exist. Please check the URL and try again.
      </CenteredMessage>
    );
  }

  if (advisorError) {
    return (
      <CenteredMessage>
        Access to the Covr platform was denied. Your login information was invalid or your current
        Covr session was timed out due to inactivity.
      </CenteredMessage>
    );
  }

  if (authState === 'invalid') {
    return (
      <CenteredMessage>
        Access to the Covr platform was denied. Your login information was invalid or your current
        Covr session was timed out due to inactivity.
      </CenteredMessage>
    );
  }

  if (advisor && !ROLES_WITH_PLATFORM_ACCESS.includes(advisor.role)) {
    return (
      <CenteredMessage>
        At this time your role has restricted access to this platform. Future enhancements coming
        soon.
      </CenteredMessage>
    );
  }

  if (accountLoading || advisorLoading || authState === 'pending' || !advisor) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Outlet />
      {!bypassAuthentication && <SessionTimeout />}
    </>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <Box display="flex" justifyContent="center" mt={4} px={2}>
      <Typography>{children}</Typography>
    </Box>
  );
}
